import type { ReactNode } from "react";
import React, { Suspense, useEffect } from "react";
import { Provider } from "react-redux";
import axios from "axios";

import { Box, BUCProvider, Loader } from "@braintrust/braintrust-ui-components";
import {
  fetchCurrentUser,
  postFreelancerLogin,
  postUserLogin,
} from "@js/apps/auth/actions";
import { useAccountType, useFreelancerId } from "@js/apps/common/hooks";
import { employerApi } from "@js/apps/employer/api";
import { fetchFreelancerProfile } from "@js/apps/freelancer/actions";
import { WebsocketManagerWrapper } from "@js/components/websocket-manager-wrapper";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import { store } from "@js/store";
import { projectTheme } from "@js/theme";

import { SmartlookProvider } from "../smartlook-provider";
import { Snackbar, StyledSnackbarProvider } from "../snackbar";
import StripeProvider from "../stripe-provider";

type AppProvidersProps = {
  children: ReactNode;
};

export const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <Provider store={store}>
      <BUCProvider theme={projectTheme}>
        <StyledSnackbarProvider>
          <InitialUserFetch>
            <WebsocketManagerWrapper>
              <StripeProvider>
                <SmartlookProvider>
                  <Suspense fallback={<Loader centered />}>{children}</Suspense>
                </SmartlookProvider>
              </StripeProvider>
            </WebsocketManagerWrapper>
          </InitialUserFetch>
        </StyledSnackbarProvider>
      </BUCProvider>
    </Provider>
  );
};

const useInitialUserFetch = () => {
  const [fetched, setFetched] = React.useState(false);
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    let isCancelled = false;

    const loadFreelancerProfile = async (freelancerId: number) => {
      const freelancer = await dispatch(fetchFreelancerProfile(freelancerId));
      if (isCancelled) {
        return;
      }

      return dispatch(postFreelancerLogin(freelancer));
    };

    const fetch = async () => {
      try {
        const user = await dispatch(fetchCurrentUser());

        if (isCancelled) {
          return;
        }

        await dispatch(postUserLogin(user));
        if (isCancelled) {
          return;
        }

        if (user.freelancer) {
          await loadFreelancerProfile(user.freelancer);
        }

        if (user.employer) {
          await dispatch(
            employerApi.endpoints.getManagedEmployerById.initiate(
              user.employer,
            ),
          );
        }

        if (isCancelled) {
          return;
        }

        setFetched(true);
      } catch (error) {
        if (axios.isAxiosError(error) && error?.response?.status === 401) {
          // unauthorized, expected (eg login view)
          setFetched(true);
          return;
        }
        Snackbar.error("Something went wrong, please refresh the page");
      }
    };

    void fetch();

    return () => {
      isCancelled = true;
    };
  }, [dispatch]);

  return fetched;
};

const useFetchMissingFreelancerProfile = (userHasBeenFetched: boolean) => {
  // const [initPayment, { data, isLoading }] = useLazyInitCoinbasePaymentQuery();
  const dispatch = useAppDispatch();

  const freelancerId = useFreelancerId();
  const isFreelancer = useAccountType();
  const freelancerProfile = useAppSelector(
    (state) => state.freelancer.freelancerProfile,
  );

  const isMissingFreelancerProfile = freelancerProfile === undefined;

  const shouldFetchFreelancerProfile =
    userHasBeenFetched && isFreelancer && isMissingFreelancerProfile;

  useEffect(() => {
    if (!shouldFetchFreelancerProfile || !freelancerId) return;

    void dispatch(fetchFreelancerProfile(freelancerId));
  }, [shouldFetchFreelancerProfile, dispatch, freelancerId]);
};

export const InitialUserFetch = ({ children }: { children: ReactNode }) => {
  const fetched = useInitialUserFetch();

  // there can be a case where user is already fetched but we are missing their freelancer profile data
  useFetchMissingFreelancerProfile(fetched);

  if (!fetched) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Loader centered />
      </Box>
    );
  }

  return children;
};
